import type { CSSProperties } from 'react';

import { cn } from '../utils/cn';

/**
 * Scrim tint shared with overlays that must drive opacity themselves
 * (e.g. the lightbox swipe-to-dismiss motion backdrop).
 *
 * Deliberately NO backdrop-filter here or in Backdrop: blur forces the
 * compositor to re-sample the live content behind the overlay every frame
 * (streaming chat, canvas avatars, websocket-driven lists), and on
 * CPU-throttled machines Chromium drops the blurred layer for frames —
 * the backdrop visibly flickers. The scrim opacity is tuned to carry the
 * separation on its own.
 */
export const BACKDROP_BG_CLASS = 'bg-backdrop';

type BackdropProps = {
	onClick?: () => void;
	/**
	 * `faint` — light veil for overlays on already-calm pages (docs search).
	 * `dialog` — canvas-colored wash under `bg-dialog` surfaces (Modal, command
	 * palette), so a translucent dialog never sits on a dark scrim.
	 */
	variant?: 'default' | 'faint' | 'dialog';
	/** Positioning/behavior only (e.g. `pointer-events-auto`), never visuals. */
	className?: string;
	style?: CSSProperties;
};

export const Backdrop = ({ onClick, variant = 'default', className, style }: BackdropProps) => (
	<div
		aria-hidden="true"
		className={cn(
			'absolute inset-0 animate-in fade-in-0 duration-200',
			variant === 'faint' && 'bg-backdrop/25',
			variant === 'dialog' && 'bg-dialog-backdrop',
			variant === 'default' && BACKDROP_BG_CLASS,
			className,
		)}
		style={style}
		onClick={onClick}
	/>
);
